import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox } from "./shared";
import { CalendarDays, Video, Phone, X, Plus, Save, Trash2 } from "lucide-react";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

export function Consultations() {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [blackout, setBlackout] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/consultations").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/consultations/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDay = (n) => setCfg((c) => ({ ...c, working_days: c.working_days.includes(n) ? c.working_days.filter((x) => x !== n) : [...c.working_days, n].sort() }));
  const addBlackout = () => { if (blackout && !cfg.blackout_dates.includes(blackout)) { setCfg((c) => ({ ...c, blackout_dates: [...c.blackout_dates, blackout].sort() })); setBlackout(""); } };
  const removeBlackout = (d) => setCfg((c) => ({ ...c, blackout_dates: c.blackout_dates.filter((x) => x !== d) }));

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data: r } = await api.put("/admin/consultations/config", {
        working_days: cfg.working_days, start_hour: Number(cfg.start_hour), end_hour: Number(cfg.end_hour),
        slot_minutes: Number(cfg.slot_minutes), blackout_dates: cfg.blackout_dates,
      });
      setCfg(r); toast.success("Availability saved.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingCfg(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/admin/consultations/${id}/cancel`); toast.success("Booking cancelled."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data || !cfg) return <Loader />;
  const items = data.consultations || [];

  return (
    <div>
      <Head title="Consultations" desc={`${data.upcoming} upcoming · ${data.count} total. Free consultation bookings from your website.`} />

      {/* Availability settings */}
      <div className="bg-navy-800 border border-gold/20 p-5 mb-8" data-testid="consult-settings">
        <div className="flex items-center gap-2 text-white font-heading text-lg mb-4"><CalendarDays size={18} className="text-gold" /> Availability</div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Working days</div>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(([lbl, n]) => (
                <button key={n} onClick={() => toggleDay(n)} data-testid={`day-${n}`}
                  className={`px-3 py-1.5 text-sm border transition-colors ${cfg.working_days.includes(n) ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{lbl}</button>
              ))}
            </div>
            <div className="flex gap-4 mt-4">
              <div><div className="text-xs text-gray-400 mb-1">Start hour</div><input type="number" min="0" max="23" value={cfg.start_hour} onChange={(e) => setCfg({ ...cfg, start_hour: e.target.value })} className="w-20 bg-navy border border-white/20 text-white p-2 text-sm" data-testid="cfg-start" /></div>
              <div><div className="text-xs text-gray-400 mb-1">End hour</div><input type="number" min="1" max="24" value={cfg.end_hour} onChange={(e) => setCfg({ ...cfg, end_hour: e.target.value })} className="w-20 bg-navy border border-white/20 text-white p-2 text-sm" data-testid="cfg-end" /></div>
              <div><div className="text-xs text-gray-400 mb-1">Slot (min)</div><input type="number" min="15" step="15" value={cfg.slot_minutes} onChange={(e) => setCfg({ ...cfg, slot_minutes: e.target.value })} className="w-20 bg-navy border border-white/20 text-white p-2 text-sm" data-testid="cfg-slot" /></div>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Times shown to visitors in {cfg.timezone_label}. Hours use 24h (9 = 9am, 17 = 5pm).</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Blackout days (no bookings)</div>
            <div className="flex gap-2 mb-3">
              <input type="date" value={blackout} onChange={(e) => setBlackout(e.target.value)} data-testid="blackout-input" className="bg-navy border border-white/20 text-white p-2 text-sm" />
              <button onClick={addBlackout} data-testid="blackout-add" className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-2 text-sm hover:bg-gold/10"><Plus size={14} /> Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {cfg.blackout_dates.length === 0 && <span className="text-xs text-gray-500">None yet.</span>}
              {cfg.blackout_dates.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-gray-300 px-2.5 py-1">{d}
                  <button onClick={() => removeBlackout(d)} className="text-gray-500 hover:text-red-400"><X size={12} /></button></span>
              ))}
            </div>
          </div>
        </div>
        <button onClick={saveCfg} disabled={savingCfg} data-testid="cfg-save" className="mt-5 inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60"><Save size={15} /> Save availability</button>
      </div>

      {/* Bookings */}
      {items.length === 0 ? <EmptyBox text="No consultations booked yet." /> : (
        <div className="space-y-3" data-testid="consult-list">
          {items.map((c) => (
            <div key={c.id} className={`bg-navy-800 border p-4 flex items-start justify-between gap-4 ${c.status === "cancelled" ? "border-white/5 opacity-50" : "border-white/10"}`} data-testid={`consult-${c.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium">{c.name}</span>
                  <span className="text-xs inline-flex items-center gap-1 text-gold border border-gold/25 px-2 py-0.5">{c.meeting_type === "Video" ? <Video size={11} /> : <Phone size={11} />} {c.meeting_type}</span>
                  {c.status === "cancelled" && <span className="text-[10px] text-red-400 border border-red-500/30 px-2 py-0.5">Cancelled</span>}
                </div>
                <div className="text-sm text-gold mt-1">{c.date} · {c.time_label} <span className="text-gray-500">({c.timezone_label})</span></div>
                <div className="text-sm text-gray-400 mt-1">{c.email}{c.phone ? ` · ${c.phone}` : ""}{c.business_name ? ` · ${c.business_name}` : ""}</div>
                {c.notes && <div className="text-sm text-gray-500 mt-1 italic">"{c.notes}"</div>}
              </div>
              {c.status !== "cancelled" && (
                <button onClick={() => cancel(c.id)} data-testid={`cancel-${c.id}`} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1.5 border border-white/10 px-3 py-1.5"><Trash2 size={13} /> Cancel</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
